import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  CloudDownload,
  CloudUpload,
  History,
  Folder,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff,
  Email,
  AttachFile,
  Storage,
  Speed,
  Security,
} from '@mui/icons-material';

interface ImportHistory {
  id: string;
  type: 'import' | 'export';
  source: string;
  status: 'completed' | 'failed' | 'in_progress';
  emailCount: number;
  startedAt: string;
  completedAt?: string;
  error?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ImportExportSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Import IMAP state
  const [imapDialogOpen, setImapDialogOpen] = useState(false);
  const [imapStep, setImapStep] = useState(0);
  const [imapConfig, setImapConfig] = useState({
    server: '',
    port: '993',
    email: '',
    password: '',
    useSSL: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [remoteFolders, setRemoteFolders] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);

  // File upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'mbox' | 'eml' | 'pst'>('mbox');
  const [exportFolders, setExportFolders] = useState<string[]>(['all']);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // History state
  const [history, setHistory] = useState<ImportHistory[]>([
    {
      id: '1',
      type: 'import',
      source: 'Gmail (IMAP)',
      status: 'completed',
      emailCount: 2450,
      startedAt: '2024-12-15T10:30:00Z',
      completedAt: '2024-12-15T11:15:00Z',
    },
    {
      id: '2',
      type: 'export',
      source: 'Full Backup (MBOX)',
      status: 'completed',
      emailCount: 5230,
      startedAt: '2024-12-10T14:00:00Z',
      completedAt: '2024-12-10T14:45:00Z',
    },
  ]);

  const localFolders = ['Inbox', 'Sent', 'Drafts', 'Spam', 'Trash', 'Archive'];

  // IMAP Import handlers
  const handleTestConnection = async () => {
    setIsConnecting(true);
    setConnectionStatus('idle');
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful connection
      setRemoteFolders(['INBOX', 'Sent', 'Drafts', 'Spam', 'Trash', '[Gmail]/All Mail', '[Gmail]/Starred']);
      setConnectionStatus('success');
      setSnackbar({ open: true, message: 'Connection successful!', severity: 'success' });
    } catch {
      setConnectionStatus('error');
      setSnackbar({ open: true, message: 'Failed to connect. Check your credentials.', severity: 'error' });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleStartImport = async () => {
    setIsImporting(true);
    setImportProgress(0);

    // Simulate import progress
    const interval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsImporting(false);
          setImapDialogOpen(false);
          setSnackbar({ open: true, message: 'Import completed successfully!', severity: 'success' });
          
          // Add to history
          setHistory(prev => [{
            id: Date.now().toString(),
            type: 'import',
            source: `${imapConfig.server} (IMAP)`,
            status: 'completed',
            emailCount: Math.floor(Math.random() * 1000) + 500,
            startedAt: new Date().toISOString(),
            completedAt: new Date().toISOString(),
          }, ...prev]);
          
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 500);
  };

  // File upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = ['.eml', '.mbox', '.pst'];
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(extension)) {
        setSnackbar({ open: true, message: 'Invalid file format. Use EML, MBOX, or PST files.', severity: 'error' });
        return;
      }
      
      setUploadedFile(file);
    }
  };

  const handleUploadFile = async () => {
    if (!uploadedFile) return;
    
    setIsUploading(true);
    setUploadProgress(0);

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          setSnackbar({ open: true, message: 'File imported successfully!', severity: 'success' });
          
          // Add to history
          setHistory(prev => [{
            id: Date.now().toString(),
            type: 'import',
            source: `File: ${uploadedFile.name}`,
            status: 'completed',
            emailCount: Math.floor(Math.random() * 500) + 100,
            startedAt: new Date().toISOString(),
            completedAt: new Date().toISOString(),
          }, ...prev]);
          
          setUploadedFile(null);
          return 100;
        }
        return prev + Math.random() * 20;
      });
    }, 300);
  };

  // Export handlers
  const handleStartExport = async () => {
    setIsExporting(true);
    setExportProgress(0);

    // Simulate export progress
    const interval = setInterval(() => {
      setExportProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsExporting(false);
          setExportDialogOpen(false);
          setSnackbar({ open: true, message: 'Export completed! Download will start automatically.', severity: 'success' });
          
          // Add to history
          setHistory(prev => [{
            id: Date.now().toString(),
            type: 'export',
            source: `${exportFormat.toUpperCase()} Export`,
            status: 'completed',
            emailCount: Math.floor(Math.random() * 2000) + 1000,
            startedAt: new Date().toISOString(),
            completedAt: new Date().toISOString(),
          }, ...prev]);
          
          return 100;
        }
        return prev + Math.random() * 10;
      });
    }, 400);
  };

  const handleDeleteHistory = (id: string) => {
    setHistory(prev => prev.filter(h => h.id !== id));
    setSnackbar({ open: true, message: 'History entry deleted', severity: 'info' });
  };

  const imapSteps = ['Server Configuration', 'Select Folders', 'Import'];

  return (
    <Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 1 }}>
        Import/Export Emails
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Import emails from other providers or export your data for backup
      </Typography>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab icon={<CloudUpload />} label="Import" iconPosition="start" />
        <Tab icon={<CloudDownload />} label="Export" iconPosition="start" />
        <Tab icon={<History />} label="History" iconPosition="start" />
      </Tabs>

      {/* Import Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* IMAP Import */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: '#e8f0fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Email sx={{ fontSize: 32, color: '#1a73e8' }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                Import from Email Provider
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Import emails from Gmail, Outlook, Yahoo, or any IMAP-enabled email provider.
                Your credentials are encrypted and only used for the import process.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip icon={<Security />} label="SSL/TLS Encrypted" size="small" />
                <Chip icon={<Speed />} label="Background Processing" size="small" />
                <Chip icon={<Storage />} label="Preserves Folder Structure" size="small" />
              </Box>
              
              <Button variant="contained" onClick={() => setImapDialogOpen(true)}>
                Start IMAP Import
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* File Upload Import */}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: '#fef7e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <AttachFile sx={{ fontSize: 32, color: '#f9ab00' }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                Import from File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload EML, MBOX, or PST files to import emails. Maximum file size: 500MB.
              </Typography>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".eml,.mbox,.pst"
                style={{ display: 'none' }}
              />
              
              {uploadedFile ? (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Selected: <strong>{uploadedFile.name}</strong> ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </Typography>
                  </Alert>
                  
                  {isUploading && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                        Importing... {Math.round(uploadProgress)}%
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      variant="contained" 
                      onClick={handleUploadFile}
                      disabled={isUploading}
                    >
                      {isUploading ? 'Importing...' : 'Start Import'}
                    </Button>
                    <Button 
                      variant="outlined" 
                      onClick={() => setUploadedFile(null)}
                      disabled={isUploading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                  Select File
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </TabPanel>

      {/* Export Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: '#e6f4ea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <CloudDownload sx={{ fontSize: 32, color: '#34a853' }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                Export Your Emails
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Download your emails in various formats for backup or migration to another provider.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Format</InputLabel>
                  <Select
                    value={exportFormat}
                    label="Format"
                    onChange={(e) => setExportFormat(e.target.value as 'mbox' | 'eml' | 'pst')}
                  >
                    <MenuItem value="mbox">MBOX</MenuItem>
                    <MenuItem value="eml">EML (Individual files)</MenuItem>
                    <MenuItem value="pst">PST (Outlook)</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Folders</InputLabel>
                  <Select
                    value={exportFolders[0]}
                    label="Folders"
                    onChange={(e) => setExportFolders([e.target.value])}
                  >
                    <MenuItem value="all">All Folders</MenuItem>
                    {localFolders.map(folder => (
                      <MenuItem key={folder} value={folder.toLowerCase()}>{folder}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeAttachments}
                    onChange={(e) => setIncludeAttachments(e.target.checked)}
                  />
                }
                label="Include attachments"
              />
              
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" color="success" onClick={() => setExportDialogOpen(true)}>
                  Start Export
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Quick Export Options */}
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 2, fontWeight: 600 }}>
          QUICK EXPORT
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {localFolders.map(folder => (
            <Paper
              key={folder}
              variant="outlined"
              sx={{
                p: 2,
                cursor: 'pointer',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minWidth: 150,
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
              onClick={() => {
                setExportFolders([folder.toLowerCase()]);
                setExportDialogOpen(true);
              }}
            >
              <Folder color="action" />
              <Typography variant="body2">{folder}</Typography>
            </Paper>
          ))}
        </Box>
      </TabPanel>

      {/* History Tab */}
      <TabPanel value={tabValue} index={2}>
        {history.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <History sx={{ fontSize: 48, color: '#dadce0', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No import/export history yet
            </Typography>
          </Paper>
        ) : (
          <List>
            {history.map((item) => (
              <Paper key={item.id} variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                <ListItem sx={{ py: 2 }}
                  secondaryAction={
                    <Tooltip title="Delete from history">
                      <IconButton size="small" onClick={() => handleDeleteHistory(item.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemIcon>
                    {item.type === 'import' ? (
                      <CloudUpload sx={{ color: '#1a73e8' }} />
                    ) : (
                      <CloudDownload sx={{ color: '#34a853' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.source}
                        </Typography>
                        <Chip
                          size="small"
                          label={item.status === 'completed' ? 'Completed' : item.status === 'failed' ? 'Failed' : 'In Progress'}
                          color={item.status === 'completed' ? 'success' : item.status === 'failed' ? 'error' : 'warning'}
                          icon={item.status === 'completed' ? <CheckCircle /> : item.status === 'failed' ? <ErrorIcon /> : <Schedule />}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                          {item.emailCount.toLocaleString()} emails • {new Date(item.startedAt).toLocaleDateString()} at {new Date(item.startedAt).toLocaleTimeString()}
                        </Typography>
                        {item.completedAt && (
                          <Typography variant="caption" color="text.secondary">
                            Duration: {Math.round((new Date(item.completedAt).getTime() - new Date(item.startedAt).getTime()) / 60000)} minutes
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </Paper>
            ))}
          </List>
        )}
      </TabPanel>

      {/* IMAP Import Dialog */}
      <Dialog open={imapDialogOpen} onClose={() => !isImporting && setImapDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import from Email Provider</DialogTitle>
        <DialogContent>
          <Stepper activeStep={imapStep} sx={{ my: 2 }}>
            {imapSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {imapStep === 0 && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                For Gmail, enable "Less secure app access" or use an App Password if 2FA is enabled.
              </Alert>
              
              <TextField
                fullWidth
                label="IMAP Server"
                placeholder="imap.gmail.com"
                value={imapConfig.server}
                onChange={(e) => setImapConfig({ ...imapConfig, server: e.target.value })}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Port"
                  value={imapConfig.port}
                  onChange={(e) => setImapConfig({ ...imapConfig, port: e.target.value })}
                  sx={{ width: 100 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={imapConfig.useSSL}
                      onChange={(e) => setImapConfig({ ...imapConfig, useSSL: e.target.checked })}
                    />
                  }
                  label="Use SSL/TLS"
                />
              </Box>
              
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={imapConfig.email}
                onChange={(e) => setImapConfig({ ...imapConfig, email: e.target.value })}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Password / App Password"
                type={showPassword ? 'text' : 'password'}
                value={imapConfig.password}
                onChange={(e) => setImapConfig({ ...imapConfig, password: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              {connectionStatus === 'success' && (
                <Alert severity="success">Connection successful! Click Next to select folders.</Alert>
              )}
              {connectionStatus === 'error' && (
                <Alert severity="error">Connection failed. Please check your credentials.</Alert>
              )}
            </Box>
          )}

          {imapStep === 1 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the folders you want to import:
              </Typography>
              
              <List dense>
                {remoteFolders.map((folder) => (
                  <ListItem key={folder} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedFolders.includes(folder)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFolders([...selectedFolders, folder]);
                            } else {
                              setSelectedFolders(selectedFolders.filter(f => f !== folder));
                            }
                          }}
                        />
                      }
                      label={folder}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button size="small" onClick={() => setSelectedFolders(remoteFolders)}>
                  Select All
                </Button>
                <Button size="small" onClick={() => setSelectedFolders([])}>
                  Clear All
                </Button>
              </Box>
            </Box>
          )}

          {imapStep === 2 && (
            <Box sx={{ pt: 2, textAlign: 'center' }}>
              {isImporting ? (
                <>
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>Importing emails...</Typography>
                  <LinearProgress variant="determinate" value={importProgress} sx={{ height: 8, borderRadius: 4 }} />
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {Math.round(importProgress)}% complete
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircle sx={{ fontSize: 48, color: '#34a853', mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>Ready to import</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFolders.length} folder(s) selected
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImapDialogOpen(false)} disabled={isImporting}>
            Cancel
          </Button>
          {imapStep === 0 && (
            <>
              <Button
                onClick={handleTestConnection}
                disabled={!imapConfig.server || !imapConfig.email || !imapConfig.password || isConnecting}
              >
                {isConnecting ? <CircularProgress size={20} /> : 'Test Connection'}
              </Button>
              <Button
                variant="contained"
                onClick={() => setImapStep(1)}
                disabled={connectionStatus !== 'success'}
              >
                Next
              </Button>
            </>
          )}
          {imapStep === 1 && (
            <>
              <Button onClick={() => setImapStep(0)}>Back</Button>
              <Button
                variant="contained"
                onClick={() => setImapStep(2)}
                disabled={selectedFolders.length === 0}
              >
                Next
              </Button>
            </>
          )}
          {imapStep === 2 && (
            <>
              <Button onClick={() => setImapStep(1)} disabled={isImporting}>Back</Button>
              <Button
                variant="contained"
                onClick={handleStartImport}
                disabled={isImporting}
              >
                Start Import
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => !isExporting && setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export Emails</DialogTitle>
        <DialogContent>
          {isExporting ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>Exporting emails...</Typography>
              <LinearProgress variant="determinate" value={exportProgress} sx={{ height: 8, borderRadius: 4 }} />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                {Math.round(exportProgress)}% complete
              </Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your emails will be exported as a {exportFormat.toUpperCase()} file.
                {exportFolders[0] === 'all' ? ' All folders' : ` ${exportFolders[0]} folder`} will be included.
              </Typography>
              
              <Alert severity="info">
                The download will start automatically when the export is complete.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleStartExport}
            disabled={isExporting}
          >
            Start Export
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportExportSettings;
